import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Download,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  Circle,
  Loader2,
  Eye,
  Ban,
  RotateCcw,
  Printer
} from 'lucide-react';

const BlankBadges = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [badges, setBadges] = useState([]);
  const [summary, setSummary] = useState({ available: 0, activated: 0, voided: 0, total: 0 });
  const [batches, setBatches] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  
  // Generate dialog
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    quantity: 50,
    code_prefix: 'REG',
    starting_number: 1,
    category_id: ''
  });
  
  // View dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get(`/events/${eventId}/categories`);
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  }, [eventId]);

  const fetchEvent = useCallback(async () => {
    try {
      const response = await api.get(`/events/${eventId}`);
      setEvent(response.data);
    } catch (error) {
      toast.error('Failed to load event');
      navigate('/admin/events');
    }
  }, [eventId, navigate]);

  const fetchBadges = useCallback(async () => {
    try {
      const params = { page, limit: 50 };
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await api.get(`/events/${eventId}/blank-badges`, { params });
      setBadges(response.data.badges || []);
      setSummary(response.data.summary || { available: 0, activated: 0, voided: 0, total: 0 });
      setBatches(response.data.batches || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      toast.error('Failed to load blank badges');
    } finally {
      setLoading(false);
    }
  }, [eventId, page, statusFilter]);

  useEffect(() => {
    fetchEvent();
    fetchCategories();
  }, [fetchEvent, fetchCategories]);

  useEffect(() => {
    fetchBadges();
  }, [fetchBadges]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    
    try {
      // Send category_id only if selected
      const payload = {
        quantity: generateForm.quantity,
        code_prefix: generateForm.code_prefix,
        starting_number: generateForm.starting_number
      };
      if (generateForm.category_id) {
        payload.category_id = generateForm.category_id;
      }
      
      const response = await api.post(`/events/${eventId}/blank-badges/generate`, payload);
      
      // Download the generated PDF
      if (response.data.combined_pdf) {
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${response.data.combined_pdf}`;
        link.download = `blank_badges_${generateForm.code_prefix}_${response.data.code_range.replace(/ /g, '_')}.pdf`;
        link.click();
      }
      
      toast.success(`Generated ${generateForm.quantity} blank badges`);
      setGenerateDialogOpen(false);
      fetchBadges();
      
      // Reset form with next starting number
      setGenerateForm(prev => ({
        ...prev,
        starting_number: prev.starting_number + prev.quantity
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate badges');
    } finally {
      setGenerating(false);
    }
  };

  const handleVoid = async (code) => {
    if (!window.confirm(`Void badge ${code}? This badge will not be usable.`)) return;
    
    try {
      await api.post(`/events/${eventId}/blank-badges/${code}/void`);
      toast.success(`Badge ${code} voided`);
      fetchBadges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to void badge');
    }
  };

  const handleReset = async (code) => {
    if (!window.confirm(`Reset badge ${code} to available?`)) return;
    
    try {
      await api.post(`/events/${eventId}/blank-badges/${code}/reset`);
      toast.success(`Badge ${code} reset to available`);
      fetchBadges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset badge');
    }
  };

  const handleDownloadUnused = async () => {
    try {
      const response = await api.get(`/events/${eventId}/blank-badges/download-unused`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `blank_badges_available_${event?.campaign_id || 'event'}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Downloaded available badges');
    } catch (error) {
      toast.error('Failed to download badges');
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get(`/events/${eventId}/blank-badges`, { params: { limit: 10000 } });
      const allBadges = response.data.badges || [];
      
      // Create CSV
      const headers = ['Registration Code', 'Status', 'Assigned To', 'Ticket ID', 'Activated At', 'Batch ID'];
      const rows = allBadges.map(b => [
        b.registration_code,
        b.status,
        b.attendee_name || '-',
        b.attendee_ticket_id || '-',
        b.activated_at ? new Date(b.activated_at).toLocaleString() : '-',
        b.batch_id?.slice(0, 8) || '-'
      ]);
      
      const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `blank_badges_report_${event?.campaign_id || 'event'}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report exported');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const handleViewBadge = async (code) => {
    try {
      const response = await api.get(`/events/${eventId}/blank-badges/${code}`);
      setSelectedBadge(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load badge details');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'available':
        return <Badge variant="outline" className="bg-gray-50"><Circle className="h-3 w-3 mr-1" /> Available</Badge>;
      case 'activated':
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" /> Activated</Badge>;
      case 'voided':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" /> Voided</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(`/admin/events/${eventId}`)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Event
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Blank Badges</h1>
              <p className="text-gray-500">{event?.event_name} - On-Spot Registration</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleDownloadUnused} disabled={summary.available === 0}>
              <Download className="h-4 w-4 mr-2" /> Download Unused
            </Button>
            <Button variant="outline" onClick={handleExport}>
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Export Report
            </Button>
            <Button onClick={() => setGenerateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Generate Badges
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-gray-900">{summary.total}</div>
              <div className="text-sm text-gray-500">Total Generated</div>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-700">{summary.available}</div>
              <div className="text-sm text-green-600">Available</div>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-700">{summary.activated}</div>
              <div className="text-sm text-blue-600">Activated</div>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-700">{summary.voided}</div>
              <div className="text-sm text-red-600">Voided</div>
            </CardContent>
          </Card>
        </div>

        {/* Badges Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Blank Badges</CardTitle>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="activated">Activated</SelectItem>
                  <SelectItem value="voided">Voided</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {badges.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Printer className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No blank badges generated yet</p>
                <p className="text-sm">Generate blank badges for on-spot registration</p>
                <Button className="mt-4" onClick={() => setGenerateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" /> Generate Badges
                </Button>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Assigned To</TableHead>
                      <TableHead>Ticket ID</TableHead>
                      <TableHead>Activated</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {badges.map((badge) => (
                      <TableRow key={badge.blank_badge_id}>
                        <TableCell className="font-mono font-bold">{badge.registration_code}</TableCell>
                        <TableCell>{getStatusBadge(badge.status)}</TableCell>
                        <TableCell>{badge.attendee_name || '-'}</TableCell>
                        <TableCell className="font-mono text-sm">{badge.attendee_ticket_id || '-'}</TableCell>
                        <TableCell>
                          {badge.activated_at 
                            ? new Date(badge.activated_at).toLocaleString('en-IN', { 
                                day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' 
                              })
                            : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {badge.status === 'activated' && (
                              <Button size="sm" variant="ghost" onClick={() => handleViewBadge(badge.registration_code)}>
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}
                            {badge.status === 'available' && (
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleVoid(badge.registration_code)}>
                                <Ban className="h-4 w-4" />
                              </Button>
                            )}
                            {badge.status === 'voided' && (
                              <Button size="sm" variant="ghost" onClick={() => handleReset(badge.registration_code)}>
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-4">
                    <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                      Previous
                    </Button>
                    <span className="px-4 py-2 text-sm">Page {page} of {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Batch History */}
        {batches.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Batch History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {batches.map((batch, idx) => (
                  <div key={batch._id || idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <span className="font-medium">Batch #{idx + 1}</span>
                      <span className="text-gray-500 ml-2">
                        {batch.created_at && new Date(batch.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex gap-4 text-sm">
                      <span className="text-gray-600">{batch.count} badges</span>
                      <span className="text-green-600">{batch.available} available</span>
                      <span className="text-blue-600">{batch.activated} used</span>
                      {batch.voided > 0 && <span className="text-red-600">{batch.voided} voided</span>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Generate Dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Blank Badges</DialogTitle>
            <DialogDescription>
              Create blank badges with QR codes for on-spot registration. Select a category to use its badge template design.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleGenerate} className="space-y-4">
            <div className="space-y-2">
              <Label>Badge Template (Category) *</Label>
              <Select
                value={generateForm.category_id}
                onValueChange={(value) => setGenerateForm({ ...generateForm, category_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category template" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: cat.category_color || '#3B82F6' }}
                        />
                        {cat.category_name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Blank badges will use this category&apos;s design template</p>
            </div>
            <div className="space-y-2">
              <Label>Quantity (1-500)</Label>
              <Input
                type="number"
                min={1}
                max={500}
                value={generateForm.quantity}
                onChange={(e) => setGenerateForm({ ...generateForm, quantity: parseInt(e.target.value) || 1 })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code Prefix</Label>
                <Input
                  value={generateForm.code_prefix}
                  onChange={(e) => setGenerateForm({ ...generateForm, code_prefix: e.target.value.toUpperCase() })}
                  placeholder="REG"
                  maxLength={10}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Starting Number</Label>
                <Input
                  type="number"
                  min={1}
                  value={generateForm.starting_number}
                  onChange={(e) => setGenerateForm({ ...generateForm, starting_number: parseInt(e.target.value) || 1 })}
                  required
                />
              </div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Preview:</strong> {generateForm.code_prefix}-{String(generateForm.starting_number).padStart(3, '0')} to {generateForm.code_prefix}-{String(generateForm.starting_number + generateForm.quantity - 1).padStart(3, '0')}
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setGenerateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={generating || !generateForm.category_id}>
                {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</> : 'Generate & Download PDF'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Badge Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Badge Details</DialogTitle>
          </DialogHeader>
          {selectedBadge && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Registration Code</Label>
                  <p className="font-mono font-bold">{selectedBadge.registration_code}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Status</Label>
                  <p>{getStatusBadge(selectedBadge.status)}</p>
                </div>
              </div>
              {selectedBadge.attendee && (
                <>
                  <hr />
                  <h4 className="font-medium">Attendee Information</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label className="text-gray-500">Name</Label>
                      <p>{selectedBadge.attendee.name}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Ticket ID</Label>
                      <p className="font-mono">{selectedBadge.attendee.ticket_id}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Email</Label>
                      <p>{selectedBadge.attendee.email}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Phone</Label>
                      <p>{selectedBadge.attendee.phone_country_code} {selectedBadge.attendee.phone_number}</p>
                    </div>
                    {selectedBadge.attendee.gender && (
                      <div>
                        <Label className="text-gray-500">Gender</Label>
                        <p className="capitalize">{selectedBadge.attendee.gender}</p>
                      </div>
                    )}
                    {selectedBadge.attendee.tshirt_size && (
                      <div>
                        <Label className="text-gray-500">T-Shirt Size</Label>
                        <p>{selectedBadge.attendee.tshirt_size}</p>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-gray-500">Activated At</Label>
                    <p>{selectedBadge.activated_at && new Date(selectedBadge.activated_at).toLocaleString()}</p>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlankBadges;
